import { Badge, Box, HStack, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Portal, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { fetchPriceBooks, type PriceBookVersionSummary } from '../../../external-endpoints/fetch-price-books';
import { priceBookLang } from '../../../lang/price-book.lang';
import { useKeystoneSession } from '../../../auth/use-keystone-session';

/** Structural view of Kitchen: the pin lands with the price-book seam, so both fields stay optional. */
export type PriceBookChipKitchen = {
	priceBookVersionId?: string | null;
	isFinalized?: boolean;
};

export type PriceBookChipProps = {
	kitchen: PriceBookChipKitchen | null;
	readonly: boolean;
	/** Version the host resolved for this design, rule matches included. Falls back to the kitchen's own pin. */
	pinnedVersionId?: string | null;
	/** Version a targeting rule would move this design to; the totals below still read the effective version. */
	candidateVersionId?: string | null;
	onSelectVersion?: (priceBookVersionId: string | null) => void;
};

export const PriceBookChip = (props: PriceBookChipProps): React.ReactElement => {
	const { kitchen, readonly, pinnedVersionId: hostPinnedVersionId, candidateVersionId = null, onSelectVersion } = props;
	const { getToken } = useKeystoneSession();
	const [versions, setVersions] = useState<Array<PriceBookVersionSummary>>([]);
	const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const load = async (): Promise<void> => {
			const [result] = await fetchPriceBooks({ getToken });
			if (cancelled || !result) return;
			setVersions(result.versions);
			setCurrentVersionId(result.currentVersionId);
		};
		load();
		return () => { cancelled = true; };
	}, [getToken]);

	// Owned by the host, which also holds the resolution a targeting rule produced; the Kitchen setter does
	// not @Publish, so reading the pin off the kitchen alone would never re-render on a change.
	const pinnedVersionId = hostPinnedVersionId === undefined ? (kitchen?.priceBookVersionId ?? null) : hostPinnedVersionId;
	const effectiveVersionId = pinnedVersionId ?? currentVersionId;

	const versionName = (versionId: string | null): string => {
		if (versionId === null) return priceBookLang.label.current;
		return versions.find((version) => version.id === versionId)?.name ?? versionId;
	};

	const changed = candidateVersionId !== null && candidateVersionId !== effectiveVersionId;
	const isCurrent = pinnedVersionId === null || pinnedVersionId === currentVersionId;

	const [enabled, disabledReason] = ((): [boolean, string] => {
		if (readonly) return [false, priceBookLang.message.readonlyDesign];
		if (kitchen?.isFinalized) return [false, priceBookLang.message.finalized];
		if (versions.length === 0) return [false, priceBookLang.message.noVersionsPublished];
		// Without a handler there is nowhere to send the choice, so the list stays inert.
		if (!onSelectVersion) return [false, priceBookLang.message.pinUnavailable];
		return [true, priceBookLang.tooltip.pinVersion];
	})();

	const effectiveName = versionName(effectiveVersionId);
	// With nothing current there is no book to name, and "(Current)" after "Follow Current" reads as noise.
	const followCurrentLabel = currentVersionId === null
		? priceBookLang.label.followCurrent
		: `${priceBookLang.label.followCurrent} (${versionName(currentVersionId)})`;
	const pillLabel = changed ? `${effectiveName} → ${versionName(candidateVersionId)}` : effectiveName;

	const stateNode = isCurrent && !changed
		? <Text fontSize="xs" fontWeight="normal" color="gray.800">{priceBookLang.label.current}</Text>
		: (
			<Box as="span" border="1px solid" borderColor="brandOrange" bg="white" color="brandOrange"
				rounded="md" px="6px" py="1px" fontSize="xs" fontWeight="normal" lineHeight="1.4"
				display="inline-flex" alignItems="center" gap="4px" cursor={enabled ? 'pointer' : 'default'}>
				{changed && <Box as="span" w="6px" h="6px" rounded="full" bg="orange.300" flexShrink={0}/>}
				<Box as="span" maxW="180px" isTruncated>{pillLabel}</Box>
			</Box>
		);

	const control = enabled
		? (
			<Menu isLazy placement="top-end">
				<MenuButton as={Box} cursor="pointer" display="inline-flex" alignItems="center" _active={{ transform: 'scale(0.98)' }}>
					<Tooltip hasArrow label={pillLabel} openDelay={250}>{stateNode}</Tooltip>
				</MenuButton>
				<Portal>
					<MenuList zIndex="popover" maxH="320px" overflowY="auto" fontSize="sm">
						{changed && <MenuItem onClick={() => onSelectVersion?.(candidateVersionId)}>{`${priceBookLang.label.apply} ${versionName(candidateVersionId)}`}</MenuItem>}
						{changed && <MenuDivider/>}
						<MenuItem onClick={() => onSelectVersion?.(null)}>{followCurrentLabel}</MenuItem>
						{versions.map((version) => (
							<MenuItem key={version.id} onClick={() => onSelectVersion?.(version.id)}>
								<HStack justifyContent="space-between" w="100%">
									<Text fontWeight={version.id === effectiveVersionId ? 'bold' : 'normal'} isTruncated>{version.name}</Text>
									{version.id === effectiveVersionId && <Badge colorScheme="blue">active</Badge>}
								</HStack>
							</MenuItem>
						))}
					</MenuList>
				</Portal>
			</Menu>
		)
		: <Tooltip hasArrow label={disabledReason} openDelay={250}>{stateNode}</Tooltip>;

	return (
		<Box h="26px" px="10px" borderTop="1px" borderColor="gray.200" flexShrink={0}
			display="flex" alignItems="center" justifyContent="space-between">
			<Text fontSize="xs" color="gray.500">{priceBookLang.label.pricing}</Text>
			{control}
		</Box>
	);
};
